import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  CtaButton,
  MiniLeague,
  NoDataFound,
  Overlays,
  ToastNotification,
} from "..";
import {
  FANTASY_ANDROID,
  FANTASY_IOS,
  FANTASY_WEBSITE,
  OPT_TYPE,
  WEB,
  WEBVIEW,
} from "../../Common/constants";
import useDataLayer from "../../Common/hooks/useDataLayer";
import useLivePoints from "../../Common/hooks/useLivePoints";
import useTournamentScanario from "../../Common/hooks/useTournamentScanario";
import {
  getLeagueTypeForDL,
  getMiniLeagueBadgeId,
  getPath,
  getPlayerFromPlayerId,
  getTranslations,
  getWebPurifyStatus,
  getYearForImagePath,
  isAndroid,
  isIOS,
  isMobile,
  isStarterLeague,
} from "../../Common/utils";
import {
  currentFixture,
  getMatchStatus,
  getMiniLeaguesLeaderBoardFilter,
  leaderBoardFixtures,
} from "../../Common/utils/fixture";
import {
  getDefaultLeagueImageName,
  getLeagueConditionClsName,
  getLeagueImageName,
  getLeagueTeamID,
  getLeagueTypeId,
  getLeagueTypeName,
  getSponsoredLeagueImageSrc,
  handleSelectedLeague,
} from "../../Common/utils/league";
import { isUserLoggedIn } from "../../Common/utils/user";
import About from "../../Components/League/LeagueMarketing/About";
import Prizes from "../../Components/League/LeagueMarketing/Prizes";
import { driver, gameplay, leaderboard, league } from "../../redux/actions";
import PageLoader from "../Loaders/PageLoader";
import MyResultsTable from "../MyResultsTable";
import NewLeagueHeader from "../NewLeagueHeader";
import OppositeTeamView from "../OppositeTeamView";
import SelectField from "../SelectField";
import ToggleSwitch from "../ToggleSwitch";
import HeadToHead from "./HeadToHead";
import HeadToHeadLeaderboard from "./HeadToHeadLeaderboard";
import useMediaQuery from "../../Common/hooks/useMediaQuery";

const WrappedLeagueMembers = () => {
  const { leagueId, type } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, pathname } = location;
  const isFeaturedMiniLeagueJoin = state?.isFeaturedMiniLeagueJoin;
  const isLeagueLandingRedirect = state?.isLeagueLandingRedirect;
  const isFromCongratulationsPage = state?.isFromCongratulationsPage;
  const isFromLeagueMarketingPage = state?.isFromLeagueMarketingPage;
  let [searchParams] = useSearchParams();
  const leagueState = useSelector((state) => state.league);
  const {
    leagueInfoSuccessData,
    leagueMarketingData,
    overallLeaderboardData,
    selfRankLeaderboardData,
    toggleSharePopup,
    showCopyPopup,
  } = leagueState;
  const commonConfigState = useSelector((state) => state.commonConfig);
  const constraintsData = commonConfigState?.data?.constraints;
  const boosters = commonConfigState?.data?.boosters;
  const leaderBoardState = useSelector((state) => state.leaderboard);
  const userState = useSelector((state) => state.user);
  const driverState = useSelector((state) => state.driver);
  const translationState = useSelector((state) => state.translation);
  const fixtureCircuitConfigState = useSelector(
    (state) => state.fixtureCircuitConfig
  );
  const fixtures = fixtureCircuitConfigState?.data?.fixtures;
  const mixApiState = useSelector((state) => state.mixapi);
  const currentGamedayId = constraintsData?.GamedayId;
  const { teamLivePointsCondition } = useLivePoints();

  /**
   * Opposite team logic
   */

  const gameplayState = useSelector((state) => state.gameplay);

  const {
    oppDetailedTeams,
    selectedOpponentBooster,
    fetchOppDetailedTeamLoading,
    oppUserGameDays,
    oppUserGameDaysSuccess,
  } = gameplayState;

  const userSocialId = userState?.data?.SocialId;

  const webConfigData = commonConfigState?.data?.config;
  const leaguesConfig = webConfigData?.leagues;
  const h2hInfoCaptionList = leaguesConfig?.h2hInfoCaptionList;

  const [oppTeamGamedayId, setOppTeamGamedayId] = useState(null);
  const [oppTeamNo, setOppTeamNo] = useState(null);
  const [oppTeamGUID, setOppTeamGUID] = useState(null);
  const [selectedTeamType, setSelectedTeamType] = useState("opposite");
  const oppositeTeamDetails = oppDetailedTeams?.[0];

  const [showOnlyMyTeams, setShowOnlyMyTeams] = useState(false);
  const [activeMiniLeagueTab, setActiveMiniLeagueTab] = useState("Results");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showOppositeTeamPopup, setShowOppositeTeamPopup] = useState(false);

  const { selectedLeague } = leagueState;

  const isHeadToHead =
    getLeagueTypeId(selectedLeague?.leagueType) === 1 || type === "h2h";

  const isMiniLeague =
    selectedLeague?.league_type === "Mini" ||
    selectedLeague?.league_type === "mini" ||
    type === "mini";

  const isSponsoredLeague =
    selectedLeague?.is_sponcer || selectedLeague?.isSponsor || false;

  const isSponsorAndPrivate =
    !!selectedLeague?.is_sponcer &&
    getLeagueTypeName(selectedLeague?.league_type) === "private";

  const getLeaderBoardFixtures = useCallback(() => {
    return isMiniLeague
      ? getMiniLeaguesLeaderBoardFilter(fixtures, selectedLeague?.raceweeks)
      : isHeadToHead
        ? getMiniLeaguesLeaderBoardFilter(
            fixtures?.filter(
              (x) =>
                x.SessionType === "Race" &&
                ["PC DONE", "PROVISIONAL POINTS"].includes(
                  getMatchStatus(x.MatchStatus)
                )
            ),
            [
              ...new Set(
                fixtures
                  ?.filter(
                    (fixture) =>
                      fixture?.RaceDay >= selectedLeague?.battleStartMatchday
                  )
                  ?.map((x) => x.GamedayId)
              ),
            ]
          )
        : leaderBoardFixtures(fixtures);
  }, [fixtures, isMiniLeague, selectedLeague, isHeadToHead]);

  const LeagueSelectField = useMemo(() => {
    const allRacesField = {
      id: 0,
      value: getTranslations("league_memebers_filter_all_races", "Overall"),
    };

    return [allRacesField, ...getLeaderBoardFixtures()];
  }, [getLeaderBoardFixtures]);
  const [selectedOption, setSelectedOption] = useState(LeagueSelectField[0]);

  const { teamNo, phaseId, gameDayId } = useParams();

  // Map leaderboard data with players from driverState
  // Shows overallLeaderboardData when showOnlyMyTeams is OFF
  // Shows selfRankLeaderboardData when showOnlyMyTeams is ON
  const mappedLeaderboardData = useMemo(() => {
    const sourceData = showOnlyMyTeams
      ? selfRankLeaderboardData
      : overallLeaderboardData;

    if (!sourceData || !Array.isArray(sourceData)) {
      return null;
    }

    return sourceData.map((row) => {
      // Map user_team (array of PlayerIds) to players array with full player objects
      const players = row.user_team
        ? row.user_team
            .map((playerId) => getPlayerFromPlayerId(driverState, playerId))
            .filter(Boolean) // Remove any null/undefined entries
        : [];

      return {
        ...row,
        players,
      };
    });
  }, [
    showOnlyMyTeams,
    overallLeaderboardData,
    selfRankLeaderboardData,
    driverState,
  ]);

  // Get user's team number from self rank data
  const userTeamNo = useMemo(() => {
    if (selfRankLeaderboardData && selfRankLeaderboardData.length > 0) {
      return selfRankLeaderboardData[0]?.team_no;
    }
    return null;
  }, [selfRankLeaderboardData]);

  const { leaderBoard, userLeaderBoard, leaderBoardSuccess, leagueInfo } =
    leaderBoardState;

  const isProfanityEnabled = mixApiState?.data?.conf?.ipe;
  const isWebPurifyCalled = selectedLeague?.isWebPurifyCalled;
  const isSystemNameUpd = selectedLeague?.isSystemNameUpd;
  const webPurifyResponse = selectedLeague?.webPurifyresponse;

  const isProvisional =
    (selectedOption?.id === 0 ||
      selectedOption?.id === currentFixture(fixtures)?.GamedayId) &&
    getMatchStatus(currentFixture(fixtures)?.MatchStatus) ===
      "PROVISIONAL POINTS";

  const webPurifyStatus = getWebPurifyStatus(
    isWebPurifyCalled,
    isSystemNameUpd,
    webPurifyResponse
  );

  const [pageNo, setPageNo] = useState(1);
  const [optType, setOptType] = useState(1);
  const [selectedGameDay, setSelectedGameDay] = useState(undefined);
  const [selectedPhase, setSelectedPhase] = useState(1);

  const pushToDataLayer = useDataLayer();
  const { isEos, lastGamedayRaceLocked } = useTournamentScanario();

  const webview = userState?.webview;
  const disableCTAs = isEos || lastGamedayRaceLocked;

  const isPrivate = () =>
    getLeagueTypeId(selectedLeague?.leagueType) === 0 ||
    type === "private" ||
    getLeagueTypeId(selectedLeague?.leagueType) === 1 ||
    type === "h2h";

  const isVIP = () => !!selectedLeague?.is_vip;

  const isPublic = () =>
    getLeagueTypeId(selectedLeague?.leagueType) === 2 ||
    type === "public" ||
    selectedLeague?.leagueType === "Global" ||
    type === "global" ||
    selectedLeague?.leagueType === "Country" ||
    type === "country" ||
    selectedLeague?.leagueType === "Team" ||
    type === "team" ||
    selectedLeague?.leagueType === "Driver" ||
    type === "driver" ||
    selectedLeague?.leagueType === "Mini" ||
    type === "mini";

  const getLeagueTypeForAPI = useCallback(() => {
    if (selectedLeague?.leagueType === "Global" || type === "global") {
      return "global";
    }
    if (selectedLeague?.leagueType === "Country" || type === "country") {
      return "country";
    }
    if (selectedLeague?.leagueType === "Driver" || type === "driver") {
      return "driver";
    }
    if (selectedLeague?.leagueType === "Team" || type === "team") {
      return "team";
    }
    if (selectedLeague?.leagueType === "Mini" || type === "mini") {
      return "mini";
    }
    if (
      getLeagueTypeId(selectedLeague?.leagueType) === 2 ||
      type === "public"
    ) {
      return "public";
    }
    if (
      getLeagueTypeId(selectedLeague?.leagueType) === 0 ||
      getLeagueTypeId(selectedLeague?.leagueType) === 3 ||
      type === "private"
    ) {
      if (selectedLeague?.isSponsor && !selectedLeague?.isJoined) {
        return "sponsor";
      }
      return "private";
    }
    return "public"; // default fallback
  }, [selectedLeague, type]);

  // leaderboardType: 1 = Overall, 2 = Gameday
  const getLeaderboardType = useCallback(() => {
    return optType === 2 ? 2 : 1;
  }, [optType]);

  const getLeagueIdForAPI = useCallback(() => {
    const leagueTypeForAPI = getLeagueTypeForAPI();
    if (leagueTypeForAPI === "global") {
      return 0;
    }
    return leagueId || selectedLeague?.leagueId || 0;
  }, [getLeagueTypeForAPI, leagueId, selectedLeague]);

  const fetchLeaderboardData = useCallback(() => {
    if (!selectedLeague) return;

    const leagueTypeForAPI = getLeagueTypeForAPI();
    const leagueIdForAPI = getLeagueIdForAPI();
    const tourGamedayId = selectedGameDay ?? 0;
    const phaseId = selectedPhase ?? 1;

    dispatch(
      league.fetchCurrentOverallLeaderboardData({
        leagueType: leagueTypeForAPI,
        optType,
        leagueId: leagueIdForAPI,
        tourGameDayId: tourGamedayId,
        phaseId,
      })
    );
  }, [
    dispatch,
    selectedLeague,
    optType,
    selectedGameDay,
    selectedPhase,
    getLeagueTypeForAPI,
    getLeagueIdForAPI,
  ]);

  const fetchSelfRankData = useCallback(() => {
    if (!selectedLeague || !userState?.data?.GUID) {
      return;
    }

    const leagueTypeForAPI = getLeagueTypeForAPI();
    const leagueIdForAPI = getLeagueIdForAPI();
    const tourGamedayId = selectedGameDay ?? 0;
    const leaderboardType = getLeaderboardType();

    dispatch(
      league.fetchCurrentUserSelfRankData({
        guid: userState.data.GUID,
        tourGamedayId,
        leaderboardType,
        leagueType: leagueTypeForAPI,
        leagueId: leagueIdForAPI,
      })
    );
  }, [
    dispatch,
    selectedLeague,
    userState?.data?.GUID,
    selectedGameDay,
    optType,
    getLeagueTypeForAPI,
    getLeagueIdForAPI,
    getLeaderboardType,
  ]);

  useEffect(() => {
    // if (!selectedLeague) {
    //   navigate(getPath(translationState, "leagues"));
    // }

    if (isPrivate() || isVIP()) {
      setSelectedGameDay(1);
      setSelectedPhase(1);
    }
    if (isPublic()) {
      setSelectedGameDay(0);
      setSelectedPhase(1);
    }

    if (state?.leagueCode) {
      dispatch(league.getLeagueInfo({ leagueCode: state.leagueCode }));
    }

    return () => {
      dispatch(league.resetLeagueInfo());
      dispatch(leaderboard.resetLeaderBoard());
    };
  }, []);

  const selectedLeagueCode = selectedLeague?.league_code;

  useEffect(() => {
    if (selectedLeagueCode) {
      dispatch(league.getLeagueInfo({ leagueCode: selectedLeagueCode }));
    }

    return () => {
      dispatch(league.resetLeagueInfo());
    };
  }, [selectedLeagueCode, dispatch]);

  const shareableLeagueName = leagueInfoSuccessData?.shareable_league_name;

  useEffect(() => {
    if ((isMiniLeague || isSponsoredLeague) && shareableLeagueName) {
      dispatch(
        league.getLeagueMarketing({
          leagueType: "static",
          leagueSlug: shareableLeagueName,
        })
      );
    }
  }, [isMiniLeague, isSponsoredLeague, shareableLeagueName, dispatch]);

  useEffect(() => {
    if (leaderBoardSuccess) {
      setPageNo((prev) => prev + 1);
    }
  }, [leaderBoardSuccess]);

  const selectedOptionId = selectedOption?.id;

  useEffect(() => {
    if (leagueId || isFromCongratulationsPage) {
      dispatch(league.resetCurrentOverallLeaderboardData());
      dispatch(league.resetCurrentUserSelfRankData());
    }
  }, [leagueId, isFromCongratulationsPage, dispatch]);

  useEffect(() => {
    if (selectedLeague) {
      // Always clear previous leaderboard data before fetching fresh data
      dispatch(league.resetCurrentOverallLeaderboardData());
      dispatch(league.resetCurrentUserSelfRankData());

      fetchLeaderboardData();
      fetchSelfRankData();
    }
  }, [
    selectedLeague,
    optType,
    selectedGameDay,
    selectedPhase,
    fetchLeaderboardData,
    fetchSelfRankData,
    leagueId,
    dispatch,
  ]);

  useEffect(() => {
    if (
      mappedLeaderboardData &&
      Array.isArray(mappedLeaderboardData) &&
      mappedLeaderboardData.length > 0
    ) {
      const firstRow = mappedLeaderboardData[0];
      setSelectedMember(firstRow);
      handleLeaderboardClick(firstRow, showOnlyMyTeams ? "user" : "opposite");
    }
  }, [mappedLeaderboardData, showOnlyMyTeams, selectedOption?.id]);

  const player = useMemo(() => {
    if (
      selectedLeague?.leagueType === "Driver" ||
      selectedLeague?.leagueType === "Team"
    ) {
      const player = driverState?.list?.find(
        selectedLeague?.leagueType,
        selectedLeague?.leagueName,
        selectedLeague?.memberCount,
        selectedLeague?.maxMembers,
        selectedLeague?.leagueVIPFlag,
        selectedLeague?.leagueAdmin,
        selectedLeague?.isAdmin,
        selectedLeague?.leagueReportFlg,
        (player) => player?.PlayerId === leagueId
      );
      return player;
    }
    return null;
  }, [leagueId, selectedLeague, driverState]);

  const PvtPubCardConfig = {
    showPvtPubCardChips: !isGlobalPublicLeague(),
    conditionClsName: getLeagueTypeId(selectedLeague?.leagueType),
    leagueName: selectedLeague?.leagueName || leagueInfo?.leagueName,
    leagueCount: leagueInfo?.memCount || selectedLeague?.memberCount,
    maxLeagueCount:
      selectedLeague?.maxMembers === 99999999
        ? getTranslations("league_card_total_team_unlimited", "Unlimited")
        : selectedLeague?.maxMembers,
    showLeagueCount: true,
    type: selectedLeague?.leagueVIPFlag
      ? 3
      : getLeagueTypeId(selectedLeague?.leagueType),
    adminIcon: selectedLeague?.isSponsor
      ? false
      : selectedLeague?.leagueAdmin ||
        selectedLeague?.isAdmin ||
        selectedLeague?.is_admin === 1,
    errorIcon:
      !!selectedLeague?.leagueReportFlg || !!selectedLeague?.isReportFlag,
    isReportFlag:
      selectedLeague?.is_report_flag === 1 ||
      selectedLeague?.isReportFlag === 1,
    conditionClsName: getLeagueConditionClsName(selectedLeague?.leagueType),
    showThumb:
      selectedLeague?.leagueVIPFlag || selectedLeague?.isSponsor
        ? true
        : getLeagueImageName(
              selectedLeague?.leagueType,
              selectedLeague?.countryCode,
              leagueId,
              webConfigData
            )
          ? true
          : false,
    imageName:
      selectedLeague?.leagueVIPFlag || selectedLeague?.isSponsor
        ? leagueId
        : getLeagueImageName(
            selectedLeague?.leagueType,
            selectedLeague?.countryCode,
            leagueId,
            webConfigData
          ),
    displayPlayerImage:
      selectedLeague?.leagueType === "Driver" ||
      selectedLeague?.leagueType === "Team"
        ? true
        : false,
    player: player,
    leagueId: leagueId,
    defaultImageName: selectedLeague?.isSponsor
      ? "/0.png"
      : getDefaultLeagueImageName(selectedLeague?.leagueType, webConfigData),
    teamID: getLeagueTeamID(selectedLeague?.leagueType, leagueId, driverState),
    imageSrc: selectedLeague?.leagueVIPFlag
      ? "vip"
      : selectedLeague?.isSponsor
        ? `${getSponsoredLeagueImageSrc(selectedLeague?.leagueType, "logo")}/`
        : "",
    showProfanityIcon:
      selectedLeague?.is_report_flag !== 1 &&
      selectedLeague?.isReportFlag !== 1 &&
      (selectedLeague?.leagueAdmin ||
        selectedLeague?.isAdmin ||
        selectedLeague?.is_admin === 1) &&
      isProfanityEnabled &&
      webPurifyStatus,
    webPurifyStatus,
    showMaxTeams: true,
    maxTeams: selectedLeague?.teamCount,
    userJoinedTeams: selectedLeague?.teamNo?.length,
    leagueBadgeId:
      selectedLeague?.leagueType === "Mini"
        ? getMiniLeagueBadgeId(
            selectedLeague?.disableonList,
            selectedLeague?.tag?.id
          )
        : selectedLeague?.disableonList
          ? 4
          : selectedLeague?.tag?.id,
    leagueType: selectedLeague?.leagueType,
  };

  const [activeIndex, setActiveIndex] = useState(0);
  const updateActiveIndex = (index, value) => {
    if (!value) {
      setActiveIndex(-1);
    } else {
      setActiveIndex(index);
    }
  };

  function isGlobalPublicLeague() {
    return (
      type === "global" ||
      type === "country" ||
      type === "team" ||
      type === "driver"
    );
  }

  const mixApiLiveBuster = mixApiState?.data?.lv;
  const isAllRaces = selectedOption?.id === 0;
  const refetchOppTeamData =
    isAllRaces && teamLivePointsCondition ? mixApiLiveBuster : null;

  useEffect(() => {
    if (selectedOption?.id > 0) {
      setOptType(2);
    } else {
      setOptType(1);
    }

    if (isPrivate()) {
      setSelectedGameDay(selectedOption?.id > 0 ? selectedOption?.id : 0);
      setSelectedPhase(1);
    }
    if (isPublic()) {
      setSelectedGameDay(selectedOption?.id > 0 ? selectedOption?.id : 0);
      setSelectedPhase(1);
    }
  }, [selectedOption]);

  const gamedayIdForDriversAndTeam = useMemo(() => {
    if (selectedOption?.id) {
      return selectedOption?.id;
    } else if (teamLivePointsCondition) {
      return currentGamedayId;
    } else {
      return LeagueSelectField?.[LeagueSelectField?.length - 1]?.id;
    }
  }, [
    selectedOption,
    teamLivePointsCondition,
    LeagueSelectField,
    currentGamedayId,
  ]);

  useEffect(() => {
    const leagueType = getLeagueTypeName(selectedLeague?.leagueType);
    const getRaceWeekDrivers = () => {
      if (!selectedOption) return;
      const payload = {
        gameDayId: gamedayIdForDriversAndTeam,
        languageCode: "en",
        livePointsBuster: teamLivePointsCondition,
      };
      if (payload?.gameDayId === 0) {
        return;
      }
      dispatch(driver.fetchRaceWeekDrivers(payload));
    };
    getRaceWeekDrivers();
  }, [
    selectedOption,
    LeagueSelectField,
    dispatch,
    selectedLeague,
    gamedayIdForDriversAndTeam,
    refetchOppTeamData,
    teamLivePointsCondition,
  ]);

  useEffect(() => {
    if (selectedGameDay !== undefined) {
      dispatch(leaderboard.resetLeaderBoard());
      // handleLoadMore();
    }
  }, [selectedGameDay]);

  const meetingFixtures = fixtures?.filter(
    (x) =>
      x?.GamedayId ==
      (selectedOption?.id
        ? selectedOption?.id
        : LeagueSelectField?.[LeagueSelectField?.length - 1]?.id)
  );
  const fixtureStart =
    meetingFixtures?.length && meetingFixtures[0]?.SessionStartDate;
  const fixtureEnd =
    meetingFixtures?.length &&
    meetingFixtures[meetingFixtures?.length - 1]?.SessionEndDate;

  const transfersLeft = oppositeTeamDetails?.usersubsleft;
  const hasMadeExtraTransfers = transfersLeft < 0;

  const playerCardConfig = {
    playerType: webConfigData?.imagePaths?.driversFront?.replace(
      "{seasonYear}",
      getYearForImagePath(webConfigData)
    ),
    ctaBtn: false,
    teamLogo: false,
    jersyNo: false,
    playerStatsInList: false,
    playerTrendsLabel: false,
    ctaAppendClass: "si-btn__link",
    nameType: "short",
  };

  const pushRaceWeekSelectDataLayer = (raceWeek, clickText) => {
    const leagueType = getLeagueTypeName(selectedLeague?.leagueType);

    const dataLayerObject = {
      pageName: "Leagues",
      actionType: `Race Selection | ${raceWeek}`,
      leagueType: !!selectedLeague?.leagueVIPFlag
        ? "VIP"
        : getLeagueTypeForDL(leagueType),
      clickText,
      path: pathname,
      platform: isAndroid()
        ? FANTASY_ANDROID
        : isIOS()
          ? FANTASY_IOS
          : FANTASY_WEBSITE,
      componentType: webview ? WEBVIEW : WEB,
    };

    pushToDataLayer("leagueLeaderboard", dataLayerObject);
  };

  const pushLeagueSettingsDataLayer = () => {
    const leagueType = getLeagueTypeName(selectedLeague?.leagueType);

    const dataLayerObject = {
      actionType: "League Settings",
      leagueType: !!selectedLeague?.leagueVIPFlag
        ? "VIP"
        : getLeagueTypeForDL(leagueType),
      pageName: "Leagues",
      path: getPath(translationState, "leaguesManage", {
        type: type,
        leagueId: leagueId,
      }),
      platform: isAndroid()
        ? FANTASY_ANDROID
        : isIOS()
          ? FANTASY_IOS
          : FANTASY_WEBSITE,
      componentType: webview ? WEBVIEW : WEB,
    };

    pushToDataLayer("leagueLeaderboard", dataLayerObject);
  };

  const handleRaceWeekSelect = (e) => {
    setPageNo(1);
    setSelectedOption(
      LeagueSelectField.find((x) => x.value === e.target.value)
    );
    pushRaceWeekSelectDataLayer(e.target.value, selectedOption?.value);
  };

  const handleSeasonSoFarRaceweekClick = (GamedayId) => {
    if (LeagueSelectField?.length === 1) {
      return;
    }
    const optionValue =
      LeagueSelectField.find((x) => x.id === GamedayId) ??
      LeagueSelectField?.[0];

    setSelectedOption(optionValue);
  };

  const handleJoinPublicLeague = () => {
    navigate(
      getPath(translationState, "selectedJoinLeague", {
        code: state.leagueCode,
      })
    );
  };

  const showLeagueSetting = () => {
    if (
      isMiniLeague ||
      isStarterLeague(selectedLeague?.league_type) ||
      selectedLeague?.is_sponcer ||
      isVIP()
    ) {
      return false;
    }
    // if (state?.leagueCode) {
    //   return false;
    // }
    return true;
  };

  const [showMiniLeagueInfo, setShowMiniLeagueInfo] = useState(false);

  function toggleMiniLeagueInfo(show = false) {
    setShowMiniLeagueInfo(show);
  }

  const [showH2HLeagueInfo, setShowH2HLeagueInfo] = useState(false);

  function toggleH2HLeagueInfo(show = false) {
    setShowH2HLeagueInfo(show);
  }

  const pushJoinPublicLeagueDataLayer = () => {
    const dataLayerObject = {
      actionType: "leagueSelect",
      clickText: getTranslations(
        "league_members_join_league_button",
        "Join League"
      ),
      path: `/leagues/join/${state?.leagueCode}`,
      pageName: "Leagues",
      leagueType: "Public",
      platform: isAndroid()
        ? FANTASY_ANDROID
        : isIOS()
          ? FANTASY_IOS
          : FANTASY_WEBSITE,
      componentType: webview ? WEBVIEW : WEB,
    };

    pushToDataLayer("joinLeague", dataLayerObject);
  };

  const pushBackButtonClickDataLayer = () => {
    const leagueType = getLeagueTypeName(selectedLeague?.leagueType);

    const dataLayerObject = {
      actionType: "Back",
      clickText: getTranslations("manage_team_back_desc", "Click to go back"),
      pageName: "Leagues",
      leagueType: !!selectedLeague?.leagueVIPFlag
        ? "VIP"
        : getLeagueTypeForDL(leagueType),
      raceName: currentFixture(fixtures)?.MeetingOfficialName,
      platform: isAndroid()
        ? FANTASY_ANDROID
        : isIOS()
          ? FANTASY_IOS
          : FANTASY_WEBSITE,
      componentType: webview ? WEBVIEW : WEB,
    };

    if (state?.fromRoute) {
      pushToDataLayer("joinLeague", {
        ...dataLayerObject,
        path: state?.fromRoute,
      });
    } else {
      pushToDataLayer("joinLeague", {
        ...dataLayerObject,
        path: getPath(translationState, "leagues"),
      });
    }
  };

  // * Fetch the opposite team when the opposite usergamedays is successful
  useEffect(() => {
    const getOppositeDetailedTeam = () => {
      if (
        !oppTeamNo ||
        !oppTeamGUID ||
        isNaN(selectedOption?.id) ||
        !driverState?.list ||
        !driverState?.selectedRaceWeek ||
        !oppUserGameDaysSuccess
      ) {
        return;
      }

      const payload = {
        opponentGuid: oppTeamGUID,
        optType: OPT_TYPE,
        teamNo: oppTeamNo,
        tourGamedayId: gamedayIdForDriversAndTeam,
        phaseId: 1,
      };

      dispatch(
        gameplay.fetchOppDetailedTeam({
          payload,
          drivers: driverState?.selectedRaceWeek,
          selectedTeam: oppTeamNo - 1,
          boosters,
        })
      );
    };

    getOppositeDetailedTeam();
  }, [
    dispatch,
    oppTeamGUID,
    oppTeamNo,
    driverState,
    LeagueSelectField,
    selectedOption,
    oppUserGameDaysSuccess,
    gamedayIdForDriversAndTeam,
    boosters,
  ]);

  useEffect(() => {
    if (!mappedLeaderboardData?.length) {
      setOppTeamNo(null);
      setOppTeamGUID(null);
      dispatch(gameplay.resetOppDetailedTeam());
    }
  }, [mappedLeaderboardData]);

  useEffect(() => {
    if (
      !leaderBoard ||
      !leaderBoard?.length ||
      isMobile() ||
      !driverState?.list ||
      !driverState?.selectedRaceWeek
    ) {
      return;
    }
    const topLeaderboardPlayer = leaderBoard?.[0];

    if (topLeaderboardPlayer?.trend === -1 || !topLeaderboardPlayer?.rank) {
      return;
    }

    const topLeaderboardTeamNo =
      topLeaderboardPlayer?.teamno || topLeaderboardPlayer?.teamNo;

    setOppTeamNo(topLeaderboardTeamNo);
    setOppTeamGUID(topLeaderboardPlayer?.guid);

    const getOppUserGameDays = () => {
      if (!topLeaderboardTeamNo || !topLeaderboardPlayer?.guid) {
        return;
      }
      const payload = {
        guid: topLeaderboardPlayer?.guid,
        optType: OPT_TYPE,
        teamNo: topLeaderboardTeamNo,
      };
      dispatch(gameplay.fetchOppUserGameDays(payload));
    };

    getOppUserGameDays();
  }, [leaderBoard, dispatch, driverState, refetchOppTeamData]);

  const handleLeaderboardClick = (data, type) => {
    setSelectedTeamType(type);
    if (isNaN(selectedOption?.id)) return;
    // if (isMobile()) {
    //   navigate(
    //     getPath(translationState, "detailedOppTeam", {
    //       gameDayId: selectedOption?.id,
    //       phaseId: 1,
    //       teamNo: data?.teamno || data?.teamNo,
    //     }),
    //     {
    //       state: {
    //         ...data,
    //         fromRoute: location?.pathname,
    //         raceWeeks: selectedLeague?.raceweek,
    //       },
    //     }
    //   );
    //   return;
    // } else {
    //   setOppTeamGamedayId(gamedayIdForDriversAndTeam);
    //   const selectedOppTeamNo = data?.teamno || data?.teamNo;

    //   setOppTeamNo(selectedOppTeamNo);
    //   setOppTeamGUID(data?.guid);

    //   const getOppUserGameDays = () => {
    //     if (!selectedOppTeamNo || !data?.guid) {
    //       return;
    //     }
    //     const payload = {
    //       guid: data?.guid,
    //       optType: OPT_TYPE,
    //       teamNo: selectedOppTeamNo,
    //     };
    //     dispatch(gameplay.fetchOppUserGameDays(payload));
    //   };

    //   getOppUserGameDays();
    // }

    setOppTeamGamedayId(gamedayIdForDriversAndTeam);
    const selectedOppTeamNo = data?.teamno || data?.teamNo || data?.team_no;

    setOppTeamNo(selectedOppTeamNo);
    setOppTeamGUID(data?.user_guid);

    const getOppUserGameDays = () => {
      if (!selectedOppTeamNo || !data?.user_guid) {
        return;
      }
      const payload = {
        guid: data?.user_guid,
        optType: OPT_TYPE,
        teamNo: selectedOppTeamNo,
      };
      dispatch(gameplay.fetchOppUserGameDays(payload));
    };

    getOppUserGameDays();
  };

  const [h2hActiveTab, setH2HActiveTab] = useState(
    searchParams.get("st") === "2" ? 2 : 1
  );
  const handleTabClick = (tab) => {
    setH2HActiveTab(tab);
  };

  const selectedOppositeTeamName = useMemo(() => {
    if (selectedTeamType === "opposite") {
      return leaderBoard?.find(
        (currentTeam) => currentTeam?.teamName === oppositeTeamDetails?.teamname
      )?.teamName;
    }
    if (selectedTeamType === "user") {
      return userLeaderBoard?.find((team) => {
        const userTeamName = team?.teamName;
        const selectedTeamname = oppositeTeamDetails?.teamname;
        return userTeamName === selectedTeamname;
      })?.teamName;
    }
  }, [leaderBoard, oppositeTeamDetails, selectedTeamType, userLeaderBoard]);

  const leaderboardTableConfig = [
    { head: "Rank", cls: "rank", key: "rank" },
    { head: "Name", cls: "name", key: "teamName" },
    { head: "Points", cls: "pts", key: "points" },
  ];

  const userRankTableConfig = [
    {
      head: "Rank",
      cls: "rank",
      key: "rank",
    },
    {
      head: "Name",
      cls: "name",
      key: "teamName",
    },
    {
      head: "Points",
      cls: "pts",
      key: "points",
    },
  ];

  if (!!selectedOppositeTeamName) {
    const rightIconTableConfig = {
      head: "",
      cls: "icon",
      key: "rightIcon",
    };
    leaderboardTableConfig.push(rightIconTableConfig);
    userRankTableConfig.push(rightIconTableConfig);
  }

  const userTeamsLength = userLeaderBoard?.length ?? 0;

  /**
   * * 628px - height of RHS with additional transfers
   * * 76px - height of accordion without the team
   * * 52px - height of each team row
   * * 16px - padding top and bottom
   */
  const leaderboardTableStyles = isMobile()
    ? {}
    : {
        overflow: "auto",
        minHeight: "auto",
        maxHeight: !!userTeamsLength
          ? 628 - 76 - 52 * userTeamsLength - 16
          : 628,
      };

  const isLoggedIn = isUserLoggedIn(userState);
  const hasUserJoined =
    selectedLeague?.isJoined ||
    selectedLeague?.isJoin ||
    !!selectedLeague?.teams?.length;

  const handleJoinMiniLeague = () => {
    if (selectedLeague?.leagueCode) {
      navigate(
        getPath(translationState, "selectedJoinLeague", {
          code: selectedLeague.leagueCode,
        })
      );
    }
  };

  const processedMarketingData = useMemo(() => {
    if (!leagueMarketingData) return null;
    return {
      about: {
        ...leagueMarketingData?.about,
      },
      prizes: {
        ...leagueMarketingData?.prizes,
        prizeList: leagueMarketingData?.prizes?.prizeList?.map((prize) => ({
          ...prize,
        })),
      },
    };
  }, [leagueMarketingData]);

  // Check if prizes data is available
  const hasPrizesData = useMemo(() => {
    if (!processedMarketingData?.prizes) return false;
    const prizes = processedMarketingData.prizes;
    return (
      prizes !== null &&
      prizes !== undefined &&
      Array.isArray(prizes.prizeList) &&
      prizes.prizeList.length > 0
    );
  }, [processedMarketingData]);

  useEffect(() => {
    if (
      (isMiniLeague || isSponsoredLeague) &&
      activeMiniLeagueTab === "Prizes" &&
      !hasPrizesData
    ) {
      setActiveMiniLeagueTab("Results");
    }
  }, [isMiniLeague, isSponsoredLeague, activeMiniLeagueTab, hasPrizesData]);

  const pushShareLeagueDataLayer = () => {
    const leagueType = getLeagueTypeName(selectedLeague?.leagueType);

    const dataLayerObject = {
      actionType: "Share league",
      clickText: getTranslations(
        "create_league_completed_confirm_button",
        "Share League"
      ),
      path: pathname,
      pageName: "Leagues", // (Ex:  Create Team)
      leagueType: !!selectedLeague?.legaueVipFlag
        ? "VIP"
        : getLeagueTypeForDL(leagueType),
      raceName: currentFixture(fixtures)?.MeetingOfficialName,
      platform: isAndroid()
        ? FANTASY_ANDROID
        : isIOS()
          ? FANTASY_IOS
          : FANTASY_WEBSITE,
      componentType: webview ? WEBVIEW : WEB,
    };

    pushToDataLayer("joinLeague", dataLayerObject);
  };

  const getShareLeagueOverlayObject = (isClose = false) => ({
    pageName: "Leagues",
    actionType: `${isClose ? "Close" : "Share League"}`,
    navigationElement: "shareleagueOverlay",
    path: pathname,
    clickText: isClose
      ? getTranslations("create_league_completed_cancel_button", "Close")
      : getTranslations(
          "create_league_completed_confirm_button",
          "Share league"
        ),
    platform: isAndroid()
      ? FANTASY_ANDROID
      : isIOS()
        ? FANTASY_IOS
        : FANTASY_WEBSITE,
    componentType: webview ? WEBVIEW : WEB,
  });

  const leagueShareData = {
    leagueName:
      selectedLeague?.league_name ||
      leagueInfo?.leagueName ||
      selectedLeague?.leagueName,
    leagueType: getLeagueTypeId(selectedLeague?.league_type),
    leagueCode: selectedLeague?.league_code,
    leagueId: selectedLeague?.league_id,
    maxTeams: selectedLeague?.formatted_member_count,
  };

  const handleShareLeagueOverlayConfirm = () => {
    const dataLayerObject = getShareLeagueOverlayObject();
    pushToDataLayer("leagueShare", dataLayerObject);
  };

  const handleShareLeagueOverlayCancel = () => {
    const dataLayerObject = getShareLeagueOverlayObject(true);
    pushToDataLayer("leagueShare", dataLayerObject);
  };

  const toggleSuccessJoinLeagueOverlay = () => {
    dispatch(league.toggleSuccessJoinLeagueOverlay());
  };

  const handleShareConfirm = () => {
    dispatch(league.toggleSharePopUpOverlay());
  };
  const showRHSView = useMediaQuery("(min-width: 1080px)");

  return (
    <>
      <div className="si-league__member-wrap">
        <NewLeagueHeader
          league={selectedLeague}
          isUserLoggedIn={isLoggedIn}
          hasUserJoined={hasUserJoined}
          isHeadToHead={isHeadToHead}
          isMiniLeague={isMiniLeague}
          isSponsoredLeague={isSponsoredLeague}
          leagueId={leagueId}
          onJoinLeague={handleJoinMiniLeague}
          isSponsorAndPrivate={isSponsorAndPrivate}
          isProvisional={isProvisional}
          backButtonClick={() => {
            pushBackButtonClickDataLayer();

            if (
              isLeagueLandingRedirect ||
              isFromCongratulationsPage ||
              isFromLeagueMarketingPage
            ) {
              navigate(getPath(translationState, "leagues"));
              return;
            }

            if (type === "mini" && isFeaturedMiniLeagueJoin) {
              navigate({
                pathname: getPath(translationState, "leagues"),
                search: "?type=mini",
              });
              return;
            }
            if (window.history.length === 2) {
              navigate({
                pathname: getPath(translationState, "leagues"),
              });
              return;
            }
            if (state?.fromRoute) {
              navigate(state?.fromRoute);
            } else {
              navigate(-1);
              dispatch(gameplay.resetOppDetailedTeam());
              // navigate(getPath(translationState, "leagues"));
            }
          }}
          onShareLeague={() => {
            pushShareLeagueDataLayer();
            navigate(
              getPath(translationState, "leagueShare", {
                leagueId: leagueId,
                type: type,
              }),
              {
                state: {
                  selectedLeague,
                },
              }
            );
            // toggleSuccessJoinLeagueOverlay();
          }}
          onLeagueSettings={() => {
            navigate(
              getPath(translationState, "leaguesManage", {
                type: type,
                leagueId: leagueId,
              })
            );
          }}
          onAddTeam={() => {
            navigate(
              getPath(translationState, "selectedJoinLeague", {
                code: selectedLeague?.league_code,
              })
            );
          }}
          activeTab={activeMiniLeagueTab}
          onTabChange={(tab) => setActiveMiniLeagueTab(tab)}
          showPrizesTab={hasPrizesData}
          showShareButton={
            !isGlobalPublicLeague() &&
            !selectedLeague?.leagueVIPFlag &&
            !selectedLeague?.is_sponcer &&
            !isMiniLeague &&
            !isVIP()
          }
          showSettingsButton={showLeagueSetting()}
        />
        {/* ===== Mini League Section ===== */}
        {(isMiniLeague || isSponsoredLeague) && (
          <>
            {/* Mini League - Results Tab */}
            {activeMiniLeagueTab === "Results" && (
              <>
                {/* Mini League Results Content */}
                <div className="si-main__container">
                  {isHeadToHead ? (
                    <HeadToHeadLeaderboard
                      activeTab={h2hActiveTab}
                      handleTabClick={handleTabClick}
                      optType={optType}
                      gameDayId={selectedGameDay}
                      phaseId={selectedPhase}
                      selectedRaceOption={selectedOption}
                      handleSeasonSoFarRaceweekClick={
                        handleSeasonSoFarRaceweekClick
                      }
                    />
                  ) : (
                    <div className="si-league__classic-wrap">
                      <div className="si-league__classic-grid">
                        <div className="si-league__classic-gridLHS">
                          {/* Mini League Controls */}
                          <div className="si-main__container">
                            <div className="si-league__member-controls">
                              <div className="si-league__member-controls-lhs">
                                <SelectField
                                  options={LeagueSelectField}
                                  asButton={true}
                                  hiddenlabel={true}
                                  extraAttri={{
                                    name: "raceweekSelect",
                                    value:
                                      selectedOption?.id !== null
                                        ? selectedOption?.id
                                        : LeagueSelectField.length > 1
                                          ? LeagueSelectField[1]?.id
                                          : 0,
                                    onChange: (e) => {
                                      const selected = LeagueSelectField?.find(
                                        (x) => x.id == e.target.value
                                      );
                                      if (selected) {
                                        setSelectedOption(selected);
                                      }
                                    },
                                  }}
                                  buttonConfig={{
                                    variant: "secondary",
                                  }}
                                />
                              </div>
                              <div className="si-league__member-controls-rhs">
                                <ToggleSwitch
                                  checked={showOnlyMyTeams}
                                  onChange={(e) =>
                                    setShowOnlyMyTeams(e.target.checked)
                                  }
                                  label={getTranslations(
                                    "toggle_show_only_my_teams",
                                    "Show only my team[s]"
                                  )}
                                  id="show-only-my-teams-toggle"
                                />
                              </div>
                            </div>
                          </div>

                          <MyResultsTable
                            style={leaderboardTableStyles}
                            tableData={mappedLeaderboardData}
                            userTeamNo={userTeamNo}
                            userSocialId={userSocialId}
                            onRowClick={(row) => {
                              setSelectedMember(row);
                              handleLeaderboardClick(
                                row,
                                showOnlyMyTeams ? "user" : "opposite"
                              );
                            }}
                            selectedMember={selectedMember}
                          />
                        </div>
                        {showRHSView && (
                          <div className="si-league__classic-gridRHS">
                            {selectedMember &&
                            (oppDetailedTeams?.length > 0 ||
                              fetchOppDetailedTeamLoading) ? (
                              <OppositeTeamView
                                selectedMember={selectedMember}
                                oppositeTeamDetails={oppositeTeamDetails}
                                selectedOpponentBooster={
                                  selectedOpponentBooster
                                }
                                fetchOppDetailedTeamLoading={
                                  fetchOppDetailedTeamLoading
                                }
                                meetingFixtures={meetingFixtures}
                                selectedOption={selectedOption}
                                userSocialId={userSocialId}
                                leagueSelectField={LeagueSelectField}
                                oppUserGameDays={oppUserGameDays}
                                showOnlyMyTeams={showOnlyMyTeams}
                                isProvisional={isProvisional}
                              />
                            ) : (
                              <NoDataFound
                                noDataCaption={getTranslations(
                                  "opponent_no_data_found_title",
                                  "No opponent team found"
                                )}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Mini League - About Tab */}
            {activeMiniLeagueTab === "About" &&
              processedMarketingData?.about && (
                <About
                  data={processedMarketingData.about}
                  leagueName={
                    selectedLeague?.leagueCode || selectedLeague?.leagueName
                  }
                  leagueInfo={
                    leagueInfoSuccessData || leagueInfo || selectedLeague
                  }
                />
              )}

            {/* Mini League - Prizes Tab */}
            {activeMiniLeagueTab === "Prizes" &&
              processedMarketingData?.prizes && (
                <Prizes
                  data={processedMarketingData.prizes}
                  leagueName={
                    selectedLeague?.leagueCode || selectedLeague?.leagueName
                  }
                />
              )}
          </>
        )}

        {/* ===== Non-Mini League Section ===== */}
        {!isMiniLeague && !isSponsoredLeague && (
          <div className="si-main__container">
            {/* <div className="si-main__row">
            {(!!selectedLeague?.leagueVIPFlag ||
              !!selectedLeague?.isSponsor) && (
              <SponsorBanner
                baseURL={
                  selectedLeague?.leagueVIPFlag
                    ? "vip"
                    : selectedLeague?.isSponsor
                    ? getSponsoredLeagueImageSrc(
                        selectedLeague?.leagueType,
                        "banner"
                      )
                    : null
                }
                mobileBaseURL={
                  selectedLeague?.leagueVIPFlag
                    ? "vip-leagues/mobile-banner"
                    : selectedLeague?.isSponsor
                    ? getSponsoredLeagueImageSrc(
                        selectedLeague?.leagueType,
                        "mobile-banner"
                      )
                    : null
                }
                VipSponsorBannerImage={`${leagueId}.png`}
                showPin={false}
                bannerInternalUrl={selectedLeague?.bannerInternalUrl}
                bannerUrl={selectedLeague?.bannerUrl}
                leagueId={leagueId}
                leagueType={selectedLeague?.leagueType}
              />
            )}
            {selectedLeague?.leagueType === "Mini" && (
              <SponsorBanner
                baseURL="mini-leagues/banner"
                mobileBaseURL="mini-leagues/mobile-banner"
                VipSponsorBannerImage={`${leagueId}.png`}
                isVipSponsorMobileBannerImage={`${leagueId}.png`}
                bannerUrl={selectedLeague?.bannerUrl}
                bannerInternalUrl={selectedLeague?.bannerInternalUrl}
                bannerUrlFlag={selectedLeague?.bannerUrlFlag}
                leagueName={selectedLeague?.leagueName}
                showPin={false}
                leagueId={leagueId}
                leagueType={selectedLeague?.leagueType}
              />
            )}
            <LeagueHeader
              titleComponent={
                <>
                  <PvtPubCardInfo
                    PvtPubCardConfig={{
                      ...PvtPubCardConfig,
                      showProvisionalPts:
                        selectedOption?.id === 0 ||
                        selectedOption?.id ===
                          currentFixture(fixtures)?.GamedayId,
                    }}
                  />
                </>
              }
              component={
                <>
                  <LeagueSelectBox
                    options={LeagueSelectField}
                    selectedOption={selectedOption}
                    handleChange={handleRaceWeekSelect}
                  />
                  {isHeadToHead && (
                    <div className="si-league__mini-info">
                      <CtaButton
                        onlyIcon={true}
                        variant={"ghost"}
                        icon="f1i-info"
                        ariaLabel={getTranslations(
                          "league_hth_info_aria_label",
                          "How to play the new head to head league" // TODO: Add key for translations
                        )}
                        onClick={() => toggleH2HLeagueInfo(true)}
                      />
                    </div>
                  )}
                  {!isGlobalPublicLeague() &&
                    !selectedLeague?.leagueVIPFlag &&
                    !selectedLeague?.isSponsor &&
                    selectedLeague?.leagueType !== "Mini" && (
                      <>
                        {!!selectedLeague?.leagueCode && (
                          <LeagueShare
                            leagueName={
                              leagueInfo?.leagueName ||
                              selectedLeague?.leagueName
                            }
                            leagueType={getLeagueTypeId(
                              selectedLeague?.leagueType
                            )}
                            leagueCode={selectedLeague?.leagueCode}
                            leagueId={selectedLeague?.leagueId}
                            maxTeams={selectedLeague?.noOfteam}
                          />
                        )}
                        {leagueInfoLoading
                          ? ""
                          : // !!leagueInfoSuccessData?.extno ||
                            showLeagueSetting() && (
                              <LeagueSetting
                                type={type}
                                leagueId={leagueId}
                                pushLeagueSettingsDataLayer={
                                  pushLeagueSettingsDataLayer
                                }
                              />
                            )}
                        {state?.leagueCode &&
                          leagueInfoSuccessData &&
                          (!leagueInfoSuccessData?.extno ||
                            (leagueInfoSuccessData?.extno !== undefined &&
                              userState?.data?.TeamCount !==
                                leagueInfoSuccessData?.extno?.length &&
                              leagueInfoSuccessData?.extno?.length <
                                leagueInfoSuccessData?.noOfteam)) && (
                            <CtaButton
                              variant={"primaryDark"}
                              disabled={disableCTAs}
                              onClick={() => {
                                pushJoinPublicLeagueDataLayer();
                                handleJoinPublicLeague();
                              }}
                            >
                              {getTranslations(
                                "league_members_join_league_button",
                                "Join League"
                              )}
                            </CtaButton>
                          )}
                      </>
                    )}
                  {leagueInfoLoading ? null : selectedLeague?.leagueType ===
                    "Mini" ? (
                    <div className="si-league__mini-info">
                      <CtaButton
                        onlyIcon={true}
                        variant={"ghost"}
                        icon="f1i-info"
                        ariaLabel={getTranslations(
                          "league_landing_mini_league_join_cta",
                          "Join"
                        )}
                        onClick={() => toggleMiniLeagueInfo(true)}
                      />
                    </div>
                  ) : null}
                </>
              }
              hasBackButton={true}
              hasCustomBackAction={true}
              onBackButtonClick={() => {
                pushBackButtonClickDataLayer();

                if (isLeagueLandingRedirect) {
                  navigate(getPath(translationState, "leagues"));
                  return;
                }

                if (type === "mini" && isFeaturedMiniLeagueJoin) {
                  navigate({
                    pathname: getPath(translationState, "leagues"),
                    search: "?type=mini",
                  });
                  return;
                }
                if (window.history.length === 2) {
                  navigate({
                    pathname: getPath(translationState, "leagues"),
                  });
                  return;
                }
                if (state?.fromRoute) {
                  navigate(state?.fromRoute);
                } else {
                  navigate(-1);
                  dispatch(gameplay.resetOppDetailedTeam());
                  // navigate(getPath(translationState, "leagues"));
                }
              }}
            />
          </div> */}
            {isHeadToHead ? (
              <>
                <HeadToHead />
              </>
            ) : (
              <div className="si-league__classic-wrap">
                <div className="si-league__classic-grid">
                  <div className="si-league__classic-gridLHS">
                    <div className="si-main__container si-main__container--lbdDropdownToggle">
                      <div className="si-league__member-controls">
                        <div className="si-league__member-controls-lhs">
                          <SelectField
                            options={LeagueSelectField}
                            asButton={true}
                            hiddenlabel={true}
                            extraAttri={{
                              name: "raceweekSelect",
                              value:
                                selectedOption?.id !== null
                                  ? selectedOption?.id
                                  : LeagueSelectField.length > 1
                                    ? LeagueSelectField[1]?.id
                                    : 0,
                              onChange: (e) => {
                                const selected = LeagueSelectField?.find(
                                  (x) => x.id == e.target.value
                                );
                                if (selected) {
                                  setSelectedOption(selected);
                                }
                              },
                            }}
                            buttonConfig={{
                              variant: "secondary",
                            }}
                          />
                        </div>
                        <div className="si-league__member-controls-rhs">
                          <ToggleSwitch
                            checked={showOnlyMyTeams}
                            onChange={(e) =>
                              setShowOnlyMyTeams(e.target.checked)
                            }
                            label={getTranslations(
                              "toggle_show_only_my_teams",
                              "Show only my team[s]"
                            )}
                            id="show-only-my-teams-toggle"
                          />
                        </div>
                      </div>
                    </div>
                    <MyResultsTable
                      style={leaderboardTableStyles}
                      tableData={mappedLeaderboardData}
                      userTeamNo={userTeamNo}
                      userSocialId={userSocialId}
                      onRowClick={(row) => {
                        setSelectedMember(row);
                        handleLeaderboardClick(
                          row,
                          showOnlyMyTeams ? "user" : "opposite"
                        );
                        if (!showRHSView) {
                          setShowOppositeTeamPopup(true);
                        }
                      }}
                      selectedMember={selectedMember}
                    />

                    {/* {getLeagueTypeId(selectedLeague?.leagueType) !== 1 &&
                      !!userLeaderBoard?.length && (
                        <div className="si-main__row">
                          <div className="si-league__member-box si-league__member-box--self">
                            <Accordion
                              activeIndex={activeIndex}
                              accordionChild={
                                <AccordionChild
                                  isActive={activeIndex === 0}
                                  updateActiveIndex={updateActiveIndex}
                                  index={0}
                                  accordionConfig={{ showAccordRHS: false }}
                                  headComponentLHS={
                                    <>
                                      <h2 className="si-accordion__header">
                                        <span>
                                          {getTranslations(
                                            "league_members_my_team_ranks_title",
                                            "My Team Ranks"
                                          )}
                                        </span>
                                      </h2>
                                    </>
                                  }
                                  bodyComponent={
                                    <>
                                      <div className="si-league__member-tbl">
                                        <TableHeader
                                          tableConfig={userRankTableConfig}
                                          tableData={userLeaderBoard}
                                          handleClick={(data) =>
                                            handleLeaderboardClick(data, "user")
                                          }
                                          selectedOppositeTeamName={
                                            selectedOppositeTeamName
                                          }
                                          selectedOption={selectedOption}
                                          hideMiniCardTeam={true}
                                        />
                                      </div>
                                    </>
                                  }
                                />
                              }
                            />
                          </div>
                        </div>
                      )}
                    {type === "h2h" || !!leaderBoard?.length ? (
                      <div
                        className="si-league__member-box"
                        style={leaderboardTableStyles}
                      >
                        <div className="si-league__member-tbl">
                          <TableHeader
                            tableConfig={leaderboardTableConfig}
                            tableData={leaderBoard}
                            selectedOption={selectedOption}
                            raceWeeks={selectedLeague?.raceweek}
                            handleClick={(data) =>
                              handleLeaderboardClick(data, "opposite")
                            }
                            selectedOppositeTeamName={selectedOppositeTeamName}
                            showNewLeaderIcon={
                              selectedOption?.value === "All Races"
                                ? true
                                : false
                            }
                          />
                        </div>
                        {hasMore && (
                          <div className="si-main__row">
                            <div className="si-league__member-cta">
                              <LoadMoreCta
                                handleLoadMore={() => handleLoadMore()}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <NoDataFound
                        noDataCaption={getTranslations(
                          "leaderboard_no_data_found_title",
                          "No leaderboard found"
                        )}
                      />
                    )} */}
                  </div>
                  {showRHSView && (
                    <div className="si-league__classic-gridRHS">
                      {selectedMember &&
                      (oppDetailedTeams?.length > 0 ||
                        fetchOppDetailedTeamLoading) ? (
                        <OppositeTeamView
                          selectedMember={selectedMember}
                          oppositeTeamDetails={oppositeTeamDetails}
                          selectedOpponentBooster={selectedOpponentBooster}
                          fetchOppDetailedTeamLoading={
                            fetchOppDetailedTeamLoading
                          }
                          meetingFixtures={meetingFixtures}
                          selectedOption={selectedOption}
                          userSocialId={userSocialId}
                          leagueSelectField={LeagueSelectField}
                          oppUserGameDays={oppUserGameDays}
                          showOnlyMyTeams={showOnlyMyTeams}
                          isProvisional={isProvisional}
                        />
                      ) : (
                        <NoDataFound
                          noDataCaption={getTranslations(
                            "opponent_no_data_found_title",
                            "No opponent team found"
                          )}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Display the opposite team here */}

      {showMiniLeagueInfo && (
        <Overlays
          overlayName="mini"
          overlayTitle={getTranslations(
            "create_mini_league_overlay_title_2",
            "Mini League"
          )}
          btnSecondary={getTranslations(
            "create_mini_league_completed_close_button",
            "Close"
          )}
          overlayConfig={{
            cancelBtn: true,
            submitBtn: false,
            showOverlaySub_Title: true,
          }}
          component={<MiniLeague leagueData={selectedLeague} />}
          onCancelClick={() => toggleMiniLeagueInfo(false)}
        />
      )}
      {showH2HLeagueInfo && (
        <ToastNotification
          type={"alert"}
          alertText={getTranslations(
            "league_hth_info_title",
            "Popup Message With Content"
          )}
          alertCaption={
            <ul>
              {h2hInfoCaptionList?.map((listItem, index) => {
                return <li key={index}>{getTranslations(listItem)}</li>;
              })}
            </ul>
          }
          isAlertCaptionHtml={true}
          handleClose={() => setShowH2HLeagueInfo(false)}
        />
      )}

      {toggleSharePopup && (
        <ToastNotification toastType={""} type={"socialsharing"} />
      )}
      {showCopyPopup && (
        <ToastNotification
          toastType={""}
          type={"generic"}
          alertText={getTranslations(
            "copied_to_clipboard_toast_message",
            "Copied to clipboard"
          )}
        />
      )}
      {showOppositeTeamPopup && !showRHSView && selectedMember && (
        <Overlays
          overlayName="oppositeTeam"
          overlayConfig={{
            cancelBtn: false,
            submitBtn: false,
            noOverlayFoot: true,
            closeBtn: false,
            showOverlayTitle: false,
          }}
          onCancelClick={() => {
            setShowOppositeTeamPopup(false);
            setSelectedMember(null);
          }}
          component={
            <div className="si-oppositeTeamView__popup-wrapper">
              <div className="si-oppositeTeamView__popup-header">
                <div className="si-oppositeTeamView__popup-header-lhs">
                  <SelectField
                    options={LeagueSelectField}
                    asButton={true}
                    hiddenlabel={true}
                    extraAttri={{
                      name: "raceweekSelect",
                      value:
                        selectedOption?.id !== null
                          ? selectedOption?.id
                          : LeagueSelectField.length > 1
                            ? LeagueSelectField[1]?.id
                            : 0,
                      onChange: (e) => {
                        const selected = LeagueSelectField?.find(
                          (x) => x.id == e.target.value
                        );
                        if (selected) {
                          setSelectedOption(selected);
                        }
                      },
                    }}
                    buttonConfig={{
                      variant: "secondary",
                    }}
                  />
                </div>
                <div className="si-oppositeTeamView__popup-header-rhs">
                  <CtaButton
                    size={"md"}
                    onlyIcon={true}
                    variant={"ghost"}
                    icon={"f1i-close"}
                    onClick={() => {
                      setShowOppositeTeamPopup(false);
                      setSelectedMember(null);
                    }}
                  />
                </div>
              </div>
              <div className="si-oppositeTeamView__popup-content">
                <OppositeTeamView
                  selectedMember={selectedMember}
                  oppositeTeamDetails={oppositeTeamDetails}
                  selectedOpponentBooster={selectedOpponentBooster}
                  fetchOppDetailedTeamLoading={fetchOppDetailedTeamLoading}
                  meetingFixtures={meetingFixtures}
                  selectedOption={selectedOption}
                  userSocialId={userSocialId}
                  leagueSelectField={LeagueSelectField}
                  oppUserGameDays={oppUserGameDays}
                  showOnlyMyTeams={showOnlyMyTeams}
                  isProvisional={isProvisional}
                />
              </div>
            </div>
          }
        />
      )}
    </>
  );
};

const LeagueMembers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { leagueId, type } = useParams();
  const userState = useSelector((state) => state.user);
  const translationState = useSelector((state) => state.translation);
  const userGUID = userState?.data?.GUID;

  const leagueState = useSelector((state) => state.league);

  const {
    selectedLeague,
    leagueLandingLoading,
    leagueLandingFulfilled,
    leagueLandingData,
  } = leagueState;

  // If selectedLeague is already present, don't perform any operations
  const isSelectedLeaguePresent =
    Number(selectedLeague?.league_id) === Number(leagueId);

  // Stub from Join page lacks teams[] needed for T1/T2/T3 — enrich from leagueLanding
  const needsTeamsEnrichment =
    isSelectedLeaguePresent &&
    Number(selectedLeague?.is_joined) === 1 &&
    (!Array.isArray(selectedLeague?.teams) ||
      selectedLeague.teams.length === 0);

  // Fetch league landing data if selectedLeague is not present or needs enrichment
  useEffect(() => {
    if ((!isSelectedLeaguePresent || needsTeamsEnrichment) && userGUID) {
      dispatch(league.getLeagueLanding({ guid: userGUID }));
    }
  }, [dispatch, userGUID, isSelectedLeaguePresent, needsTeamsEnrichment]);

  // Once league landing data is fetched, find the league and set selectedLeague
  useEffect(() => {
    if (!leagueLandingFulfilled || !leagueLandingData) return;

    const userLeagues = leagueLandingData?.user_leagues || [];
    const foundLeague = userLeagues.find(
      (leagueItem) => String(leagueItem?.league_id) === String(leagueId)
    );

    if (!isSelectedLeaguePresent) {
      if (foundLeague) {
        handleSelectedLeague(foundLeague, dispatch);
      } else {
        navigate(getPath(translationState, "leagues"));
      }
    } else if (needsTeamsEnrichment) {
      // Guard against re-render loop if teams come back empty
      if (foundLeague?.teams?.length > 0) {
        handleSelectedLeague({ ...selectedLeague, ...foundLeague }, dispatch);
      }
    }
  }, [
    isSelectedLeaguePresent,
    needsTeamsEnrichment,
    leagueLandingFulfilled,
    leagueLandingData,
    leagueId,
    selectedLeague,
    dispatch,
    navigate,
    translationState,
  ]);

  // Show loader while fetching league landing data or processing
  if (!isSelectedLeaguePresent) {
    if (leagueLandingLoading || !leagueLandingFulfilled) {
      return <PageLoader />;
    }
  }

  return <WrappedLeagueMembers />;
};

export { LeagueMembers };
